import { AlertCircle, AlertTriangle, Loader2, Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { PriceDisplay } from "@/components/ui/price-display";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSalonConfig } from "@/domains/settings/features/salon-config/hooks/use-salon-config";
import { cn } from "@/lib/utils";
import { useServicesStep } from "../../../hooks/use-services-step";
import { ServiceWorkerAssignment, type Worker } from "./service-worker-assignment";

interface DesktopServicesStepProps {
  servicesStep: ReturnType<typeof useServicesStep>;
  loading?: boolean;
}

export function DesktopServicesStep({ servicesStep, loading = false }: DesktopServicesStepProps) {
  const { t } = useTranslation("appointments");
  const { config } = useSalonConfig();
  const {
    selectedServices,
    setSelectedServices,
    searchTerm,
    setSearchTerm,
    selectedCategory,
    setSelectedCategory,
    categories,
    filteredServices,
    handleServiceToggle,
    removeService,
    isServiceSelected,
    workers,
    loading: servicesLoading,
    getValidationError,
    getValidationWarning,
  } = servicesStep;

  return (
    <div className="space-y-6" data-test-id={`appointment-create-desktop-services-step`}>
      <div>
        <h3 className="text-lg font-semibold mb-4">
          {t("booking.servicesStep.labels.servicesSelection")}
        </h3>

        {/* Selected Services Summary */}
        <div className="space-y-4 mb-6">
          <div className="flex flex-wrap gap-2 min-h-[32px] items-start">
            {selectedServices.length > 0 ? (
              selectedServices.map((service) => (
                <Badge
                  key={service.id}
                  variant="secondary"
                  className="text-xs flex items-center gap-1"
                  data-test-id={`appointment-create-selected-service-${service.id}`}
                >
                  {service.name} - <PriceDisplay amount={service.price} />
                  <button
                    type="button"
                    onClick={() => removeService(service.id)}
                    className="ml-1 hover:bg-accent rounded-full p-0.5"
                    data-test-id={`appointment-create-remove-service-${service.id}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))
            ) : (
              <span className="text-sm text-muted-foreground py-1">
                {t("booking.servicesStep.labels.selectAtLeastOne")}
              </span>
            )}
          </div>
        </div>

        {/* Search and Filters */}
        <div className="space-y-4 mb-4">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder={t("booking.servicesStep.labels.searchServices")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-test-id="appointment-create-service-search"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Services List */}
        <div className="border rounded-lg max-h-80 overflow-y-auto bg-card scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent">
          {servicesLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              <span>{t("booking.servicesStep.labels.loadingServices")}</span>
            </div>
          ) : filteredServices.length === 0 ? (
            <div className="text-center p-8 text-muted-foreground">
              {searchTerm || selectedCategory !== t("booking.servicesStep.categories.all")
                ? t("booking.servicesStep.messages.noServicesFound")
                : t("booking.servicesStep.messages.noServicesAvailable")}
            </div>
          ) : (
            <div className="divide-y">
              {filteredServices.map((service: any) => {
                const isSelected = isServiceSelected(service.id);
                return (
                  <div
                    key={service.id}
                    role="option"
                    aria-selected={isSelected}
                    tabIndex={0}
                    className={cn(
                      "w-full text-left p-4 hover:bg-accent cursor-pointer transition-colors",
                      isSelected && "bg-accent border-l-4 border-l-primary"
                    )}
                    onClick={() => handleServiceToggle(service)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleServiceToggle(service);
                      }
                    }}
                    data-test-id={`appointment-create-service-${service.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleServiceToggle(service)}
                          className="pointer-events-none"
                        />
                        <div>
                          <div className="font-medium">{service.name}</div>
                          <div className="text-sm text-muted-foreground flex items-center gap-3">
                            <PriceDisplay amount={service.basePrice} />
                            {service.category?.name && (
                              <Badge variant="outline" className="text-xs">
                                {service.category.name}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {/* Worker Assignment */}
        {config.appointmentWorkerAssignmentEnabled && selectedServices.length > 0 && (
          <div className="mt-8">
            <ServiceWorkerAssignment
              selectedServices={selectedServices}
              workers={workers as unknown as Worker[]}
              onServicesChange={setSelectedServices}
              loading={loading || servicesLoading}
            />
          </div>
        )}

        {/* Validation Messages */}
        {selectedServices.length > 0 && (
          <div className="mt-6 space-y-4">
            {/* Error Message (for non-owners) */}
            {getValidationError() && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{getValidationError()}</AlertDescription>
              </Alert>
            )}

            {/* Warning Message (for owners) */}
            {getValidationWarning() && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{getValidationWarning()}</AlertDescription>
              </Alert>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
